import { BatchItem, BatchStatus } from '../services/agent.service';

/**
 * What a toast says about one invoice, or one finished batch.
 *
 * <p>Pure, so the wording is testable without a running app — and separate from the tracker so
 * the tracker's tests are about polling rather than about prose.
 *
 * <p><b>Deliberately shorter than the chat line.</b> A toast is read in passing, often on another
 * page: the file name and what happened to it, and nothing else. The detail is in the panel,
 * which is where someone goes when a toast tells them to look.
 */

export type ToastKind = 'success' | 'warning' | 'error' | 'info';

export interface BatchToast {
  kind: ToastKind;
  message: string;
  title: string;
}

/** The toast for one invoice reaching a terminal state, or null when it deserves none. */
export function itemToast(item: BatchItem): BatchToast | null {
  switch (item.state) {
    case 'created':
      return {
        kind: 'success',
        title: 'Draft created',
        message: `${item.fileName}${item.apNumber ? ` → ${item.apNumber}` : ''}`,
      };

    case 'needs-attention':
      return {
        kind: 'warning',
        title: 'Draft needs a check',
        message: `${item.fileName}: ${item.reason ?? 'check the draft before submitting'}`,
      };

    case 'failed':
      return {
        kind: 'error',
        title: 'Could not create a draft',
        message: `${item.fileName}: ${item.reason ?? 'no reason given'}`,
      };

    case 'skipped':
      // A split parent is not something to interrupt anyone about: its children each produce
      // their own toast, and three of them plus "this file holds three invoices" is noise.
      if (item.reason && /holds \d+ invoices/i.test(item.reason)) return null;

      return {
        kind: 'info',
        title: 'Already exists',
        message: `${item.fileName}: ${item.reason ?? 'a matching invoice already exists'}`,
      };

    default:
      return null;
  }
}

/**
 * The toast for a whole batch draining.
 *
 * <p>One line, because by this point the per-invoice toasts have already said the detail. This
 * exists so someone who was on another page the whole time gets a single summary rather than
 * having to count the toasts they half-noticed.
 */
export function batchToast(status: BatchStatus): BatchToast {
  const created = status.summary.created + status.summary['needs-attention'];

  if (status.status === 'cancelled') {
    return {
      kind: 'info',
      title: 'Upload stopped',
      message: `${created} of ${status.total} draft(s) were created before you stopped it.`,
    };
  }

  if (created === 0) {
    return {
      kind: 'error',
      title: 'No drafts created',
      message: `None of the ${status.total} invoice(s) could be turned into a draft.`,
    };
  }

  const needsCheck = status.summary['needs-attention'];

  return {
    kind: needsCheck || status.summary.failed ? 'warning' : 'success',
    title: 'Invoices processed',
    message:
      `${created} of ${status.total} draft(s) created` +
      (needsCheck ? `, ${needsCheck} need a check` : '') +
      (status.summary.failed ? `, ${status.summary.failed} failed` : '') +
      '. Review them in Accounts Payable.',
  };
}
